import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import * as HiIcons from 'react-icons/hi'
import useLocale from '../../hooks/useLocale'
import useTranslation from '../../hooks/useTranslation'
import useOutsideClick from '../../hooks/useOutsideClick'
import { media } from '../../styles/media'

/*
 * MonthFilter — the month a transaction list is showing.
 *
 * A ledger, a bank account or an invoice register grows without end, so the
 * useful question is almost never "everything" but "this month". The component
 * sits at the right end of the search row, opposite the search button, and
 * narrows the list to one calendar month: the arrows step a month at a time,
 * the label opens a year-and-month picker for a jump further back, and the dot
 * returns to the current month from wherever the user has wandered.
 *
 * Which lists show it is config, not code — see `applyMonthFilter` in
 * listViewData. The selected month itself is owned by ListView, which persists
 * it and turns it into the date range the query filters on.
 *
 * `value` is `{ year, month }` (month is 0-11, as Date counts them) or the
 * string 'all'. 'all' is the deliberate escape hatch: a month filter that
 * cannot be switched off would also narrow every search.
 */

const Wrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 40px;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 20px;
    background-color: transparent;
    color: ${({ theme }) => theme.font.color};
`

const StepButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0 0.125rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: transparent;
    color: ${({ theme }) => theme.font.color};
    font-size: 1rem;
    cursor: pointer;
    transition: background-color 0.1s ease-out, opacity 0.1s ease-out;

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.dropdown.active};
    }

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }
`

// The two arrows and the shortcut, which the collapsed control drops on a phone
// — there the search row has no width to spare, so the month label alone carries
// the control and the picker does the navigating. The same button inside the
// picker keeps its place: that surface has room.
const RowButton = styled(StepButton)`
    ${media.phone} { display: none; }
`

const MonthButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 8.5rem;
    height: 32px;
    padding: 0 0.5rem;
    border: none;
    border-radius: 16px;
    background-color: ${({ $open, theme }) => $open ? theme.dropdown.active : 'transparent'};
    color: ${({ theme }) => theme.font.color};
    font-family: "Euclid Circular B", sans-serif;
    font-size: var(--font-XS);
    line-height: 1.5;
    white-space: nowrap;
    cursor: pointer;
    justify-content: center;

    &:hover {
        background-color: ${({ theme }) => theme.dropdown.active};
    }

    /* On a phone this is the whole control, so it carries the pill's padding
       rather than sitting between two arrows. */
    ${media.phone} {
        min-width: 0;
        padding: 0 0.75rem;
    }
`

const LongLabel = styled.span`
    ${media.phone} { display: none; }
`

const ShortLabel = styled.span`
    display: none;
    ${media.phone} { display: inline; }
`

// The shortcut back to the current month. It stays in place when it is not
// available rather than disappearing, so the control never changes width.
const TodayButton = styled(RowButton)`
    font-size: 1.1rem;
`

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 0.25rem);
    right: 0;
    z-index: 1300;
    width: 17rem;
    padding: 0.5rem;
    background-color: ${({ theme }) => theme.dropdown.background};
    border: 1px solid ${({ theme }) => theme.dropdown.border};
    border-radius: 0.375rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);

    ${media.phone} {
        width: 15rem;
    }
`

const YearRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.25rem 0.5rem 0.25rem;
`

const YearLabel = styled.span`
    font-size: var(--font-S);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
`

const MonthGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.25rem;
`

const MonthCell = styled.button`
    height: 2.25rem;
    padding: 0;
    border: 1px solid ${({ $current, theme }) => $current ? theme.form.activeBorder : 'transparent'};
    border-radius: 0.375rem;
    background-color: ${({ $selected, theme }) => $selected ? theme.dropdown.selectedBackground : 'transparent'};
    color: ${({ $selected, theme }) => $selected ? theme.dropdown.selectedFont : theme.font.color};
    font-family: "Euclid Circular B", sans-serif;
    font-size: var(--font-XS);
    cursor: pointer;
    transition: background-color 0.1s ease-out;

    &:hover {
        background-color: ${({ theme }) => theme.dropdown.active};
    }
`

const Divider = styled.hr`
    height: 0;
    margin: 0.5rem 0;
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.dropdown.divider};
    opacity: 1;
`

const AllMonths = styled.button`
    display: block;
    width: 100%;
    height: 2.25rem;
    padding: 0 0.5rem;
    border: none;
    border-radius: 0.375rem;
    background-color: ${({ $selected, theme }) => $selected ? theme.dropdown.selectedBackground : 'transparent'};
    color: ${({ $selected, theme }) => $selected ? theme.dropdown.selectedFont : theme.font.color};
    font-family: "Euclid Circular B", sans-serif;
    font-size: var(--font-XS);
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.dropdown.active};
    }
`

// The month a list opens on when the user has never chosen one.
export const thisMonth = () => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
}

// A real month rather than 'all' — or rather than whatever a stale localStorage
// entry from an older version of this component turns out to hold.
export const isMonth = (value) => Boolean(value) && typeof value === 'object'
    && Number.isInteger(value.year) && Number.isInteger(value.month)

const MonthFilter = ({ value, onChange, testId }) => {
    const { t } = useTranslation()
    const { currentOrgLang } = useLocale()

    const [open, setOpen] = useState(false)
    const [pickerYear, setPickerYear] = useState(() => (isMonth(value) ? value.year : thisMonth().year))
    const wrapperRef = useRef(null)

    // Stable, so the document listener is not re-subscribed on every render of
    // the toolbar this sits in.
    const close = useCallback(() => setOpen(false), [])
    useOutsideClick(wrapperRef, close)

    // The picker always opens on the year being shown, not on the year the user
    // happened to page to the last time they had it open.
    useEffect(() => {
        if (open) setPickerYear(isMonth(value) ? value.year : thisMonth().year)
    }, [open])

    const current = thisMonth()
    const onCurrentMonth = isMonth(value) && value.year === current.year && value.month === current.month

    const monthNames = useMemo(() => {
        const format = new Intl.DateTimeFormat(currentOrgLang, { month: 'short' })
        return Array.from({ length: 12 }, (_, m) => format.format(new Date(2000, m, 1)))
    }, [currentOrgLang])

    const labels = useMemo(() => {
        if (!isMonth(value)) {
            const all = t('all.list.month.all')
            return { long: all, short: all }
        }
        const day = new Date(value.year, value.month, 1)
        return {
            long: new Intl.DateTimeFormat(currentOrgLang, { month: 'long', year: 'numeric' }).format(day),
            short: new Intl.DateTimeFormat(currentOrgLang, { month: 'short', year: '2-digit' }).format(day)
        }
    }, [value, currentOrgLang, t])

    // Stepping from "all months" starts at the current month, so the arrows
    // always move somewhere predictable.
    const step = (delta) => {
        const base = isMonth(value) ? value : current
        const stepped = new Date(base.year, base.month + delta, 1)
        onChange({ year: stepped.getFullYear(), month: stepped.getMonth() })
    }

    const pick = (next) => {
        onChange(next)
        setOpen(false)
    }

    return (
        <Wrapper
            ref={wrapperRef}
            name='monthFilter'
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false) }}
            data-testid={testId}
        >
            <RowButton
                type='button'
                onClick={() => step(-1)}
                title={t('all.list.month.previous')}
                aria-label={t('all.list.month.previous')}
            >
                <HiIcons.HiChevronLeft />
            </RowButton>

            <MonthButton
                type='button'
                $open={open}
                onClick={() => setOpen(!open)}
                title={t('all.list.month.pick')}
                aria-haspopup='true'
                aria-expanded={open}
            >
                <LongLabel>{labels.long}</LongLabel>
                <ShortLabel>{labels.short}</ShortLabel>
                <HiIcons.HiChevronDown />
            </MonthButton>

            <RowButton
                type='button'
                onClick={() => step(1)}
                title={t('all.list.month.next')}
                aria-label={t('all.list.month.next')}
            >
                <HiIcons.HiChevronRight />
            </RowButton>

            <TodayButton
                type='button'
                disabled={onCurrentMonth}
                onClick={() => onChange(current)}
                title={t('all.list.month.current')}
                aria-label={t('all.list.month.current')}
            >
                <HiIcons.HiCalendar />
            </TodayButton>

            {open && (
                <Menu name='monthFilterMenu'>
                    <YearRow>
                        <StepButton
                            type='button'
                            onClick={() => setPickerYear(pickerYear - 1)}
                            title={t('all.list.month.year.previous')}
                            aria-label={t('all.list.month.year.previous')}
                        >
                            <HiIcons.HiChevronLeft />
                        </StepButton>
                        <YearLabel>{pickerYear}</YearLabel>
                        <StepButton
                            type='button'
                            onClick={() => setPickerYear(pickerYear + 1)}
                            title={t('all.list.month.year.next')}
                            aria-label={t('all.list.month.year.next')}
                        >
                            <HiIcons.HiChevronRight />
                        </StepButton>
                    </YearRow>

                    <MonthGrid>
                        {monthNames.map((name, month) => (
                            <MonthCell
                                key={month}
                                type='button'
                                $selected={isMonth(value) && value.year === pickerYear && value.month === month}
                                $current={current.year === pickerYear && current.month === month}
                                onClick={() => pick({ year: pickerYear, month })}
                            >
                                {name}
                            </MonthCell>
                        ))}
                    </MonthGrid>

                    <Divider />

                    <AllMonths
                        type='button'
                        $selected={!isMonth(value)}
                        onClick={() => pick('all')}
                    >
                        {t('all.list.month.all')}
                    </AllMonths>
                </Menu>
            )}
        </Wrapper>
    )
}

export default MonthFilter
